/**
 * Gemini API Service
 * Provides functions to interact with Google Gemini API for generating business suggestions
 */

/**
 * List available Gemini models
 */
export async function listAvailableModels(): Promise<{
  success: boolean;
  models?: Array<{ name: string; displayName: string; supportedMethods: string[] }>;
  message?: string;
}> {
  try {
    const apiKey = import.meta.env.VITE_GEMINI_API_KEY;

    if (!apiKey) {
      return {
        success: false,
        message: 'Gemini API key is not configured.',
      };
    }

    const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models?key=${apiKey}`;

    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        message: errorData.error?.message || `API request failed with status ${response.status}`,
      };
    }

    const data = await response.json();

    if (data.models && Array.isArray(data.models)) {
      const models = data.models
        .filter((model: any) =>
          model.supportedGenerationMethods?.includes('generateContent')
        )
        .map((model: any) => ({
          name: model.name.replace('models/', ''),
          displayName: model.displayName || model.name,
          supportedMethods: model.supportedGenerationMethods || [],
        }));

      return {
        success: true,
        models,
      };
    }

    return {
      success: false,
      message: 'No models found in API response',
    };
  } catch (error) {
    console.error('Error listing Gemini models:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to list models',
    };
  }
}

export interface BusinessIdea {
  concept: string;
  industry?: string;
  targetAudience?: string;
  additionalInfo?: string;
}

export interface DomainSuggestion {
  domain: string;
  reasoning: string;
  tld: string;
}

export interface RelatedService {
  title: string;
  path: string;
  description: string;
}

export interface BusinessSuggestion {
  domainSuggestions: DomainSuggestion[];
  businessRecommendations: string[];
  marketingTips: string[];
  nextSteps: string[];
  relatedServices?: RelatedService[];
}

/**
 * Generate business suggestions using Gemini API
 */
export async function generateBusinessSuggestions(
  idea: BusinessIdea
): Promise<{
  success: boolean;
  data?: BusinessSuggestion;
  message?: string;
}> {
  try {
    const apiKey = import.meta.env.VITE_GEMINI_API_KEY;

    if (!apiKey) {
      return {
        success: false,
        message: 'Gemini API key is not configured. Please set VITE_GEMINI_API_KEY in your environment variables.',
      };
    }

    // Construct the prompt for Gemini
    const prompt = `You are a business consultant and domain expert for SAN micron. Based on the following business idea, provide comprehensive suggestions:

Business Concept: ${idea.concept}
${idea.industry ? `Industry: ${idea.industry}` : ''}
${idea.targetAudience ? `Target Audience: ${idea.targetAudience}` : ''}
${idea.additionalInfo ? `Additional Information: ${idea.additionalInfo}` : ''}

Please provide:
1. 5-8 domain name suggestions with different TLDs (.com, .net, .io, .co, .app, etc.). For each domain, provide:
   - The full domain name
   - A brief reasoning (1-2 sentences) explaining why this domain fits the business
   - The TLD used

2. 3-5 business recommendations (strategic advice for launching/developing this business). **IMPORTANT: Naturally mention how SAN micron's specific services can help in these recommendations.**

3. 3-5 marketing tips (how to promote this business). **IMPORTANT: Naturally mention how SAN micron's digital marketing and branding services fits here.**

4. 3-5 next steps (actionable items to get started). **IMPORTANT: include steps like "Get a professional email with SAN micron" or "Launch your site with SAN micron Web Development".**

Format your response as a COMPLETE, VALID JSON object with this exact structure:
{
  "domainSuggestions": [
    {
      "domain": "example.com",
      "reasoning": "Brief explanation",
      "tld": "com"
    }
  ],
  "businessRecommendations": ["recommendation 1", "recommendation 2"],
  "marketingTips": ["tip 1", "tip 2"],
  "nextSteps": ["step 1", "step 2"],
  "relatedServices": [
    {
      "title": "Service Title",
      "path": "/service-path",
      "description": "Why this service is relevant"
    }
  ]
}

Available SAN micron Services (MUST RECOMMEND AT LEAST 2-3 RELEVANT ONES AND MENTION THEM IN TIPS/STEPS):
- Email Hosting (/email-hosting): Professional email for business
- Digital Marketing (/marketing): Growth and brand visibility
- Website Development (/websites): Custom web solutions
- WordPress Hosting (/wordpress-hosting): Optimized WordPress speed
- Linux Hosting & Cloud Servers (/servers): High-performance server infrastructure
- Web Hosting (/web-hosting): Reliable hosting for all needs
- VOIP Services (/voip-service): Advanced business communication
- E-commerce Solutions (/e-commerce-solutions): Online store development
- AI Solutions (/ai-solutions): Intelligent automation and AI tools
- Cloud Solutions (/cloud-solutions): Scalable cloud infrastructure
- UI/UX Design (/ui-ux-design): User-centered design services
- Cyber Security (/cybersecurity-services): Advanced security protection
- SEO & Digital Presence (/simple-seo): Search engine optimization

CRITICAL: Return ONLY valid, complete JSON. Do not include any text before or after the JSON. Ensure all quotes are properly escaped and all brackets are closed.`;

    // Try available models in order of preference
    // Common available models: gemini-2.5-flash, gemini-3-flash, gemini-2.5-pro, gemini-pro
    const modelsToTry = [
      'gemini-2.5-flash',  // Fast and cost-effective
      'gemini-3-flash',     // Latest flash model
      'gemini-2.5-pro',     // Better quality
      'gemini-pro',         // Fallback
      'gemini-1.5-pro',     // Alternative fallback
    ];

    let lastError: string = '';

    // Try each model until one works
    for (const modelName of modelsToTry) {
      try {
        const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/${modelName}:generateContent?key=${apiKey}`;

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contents: [
              {
                parts: [
                  {
                    text: prompt,
                  },
                ],
              },
            ],
            generationConfig: {
              temperature: 0.7,
              topK: 40,
              topP: 0.95,
              maxOutputTokens: 4096,
            },
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          lastError = errorData.error?.message || `API request failed with status ${response.status}`;

          // If model not found, try next model
          if (response.status === 404 || lastError.includes('not found')) {
            continue;
          }

          // For other errors, return immediately
          return {
            success: false,
            message: lastError,
          };
        }

        const data = await response.json();

        // Check if response was truncated
        const candidate = data.candidates?.[0];
        if (candidate?.finishReason && candidate.finishReason !== 'STOP') {
          console.warn('Response may be incomplete. Finish reason:', candidate.finishReason);
        }

        // Extract text from Gemini response
        const textContent =
          candidate?.content?.parts?.[0]?.text || '';

        if (!textContent) {
          return {
            success: false,
            message: 'No response generated from Gemini API',
          };
        }

        // Parse JSON from the response text
        let jsonText = textContent.trim();

        // Remove markdown code blocks if present
        if (jsonText.startsWith('```json')) {
          jsonText = jsonText.replace(/^```json\n?/, '').replace(/\n?```$/, '');
        } else if (jsonText.startsWith('```')) {
          jsonText = jsonText.replace(/^```\n?/, '').replace(/\n?```$/, '');
        }

        // Try to fix incomplete JSON if needed
        // If JSON is incomplete, try to extract what we can
        let parsedData: BusinessSuggestion | null = null;

        try {
          parsedData = JSON.parse(jsonText) as BusinessSuggestion;
        } catch (parseError) {
          // If JSON parsing fails, try to extract partial data
          console.warn('JSON parse error, attempting to extract partial data:', parseError);

          // Try to find and extract the JSON object even if incomplete
          const jsonMatch = jsonText.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            let partialJson = jsonMatch[0];

            // Try to close incomplete strings/arrays/objects
            try {
              // Count unclosed brackets
              const openBraces = (partialJson.match(/\{/g) || []).length;
              const closeBraces = (partialJson.match(/\}/g) || []).length;
              const openBrackets = (partialJson.match(/\[/g) || []).length;
              const closeBrackets = (partialJson.match(/\]/g) || []).length;

              // Close incomplete arrays
              for (let i = 0; i < openBrackets - closeBrackets; i++) {
                partialJson += ']';
              }

              // Close incomplete objects
              for (let i = 0; i < openBraces - closeBraces; i++) {
                partialJson += '}';
              }

              // Try to close incomplete strings (find last unclosed quote)
              const lastQuoteIndex = partialJson.lastIndexOf('"');
              const quotesBefore = (partialJson.substring(0, lastQuoteIndex).match(/"/g) || []).length;
              if (quotesBefore % 2 !== 0) {
                // Unclosed string, try to close it
                partialJson = partialJson.substring(0, lastQuoteIndex + 1) + '"' + partialJson.substring(lastQuoteIndex + 1);
              }

              parsedData = JSON.parse(partialJson) as BusinessSuggestion;
            } catch (recoveryError) {
              // If recovery fails, try to extract just the domainSuggestions
              const domainMatch = jsonText.match(/"domainSuggestions"\s*:\s*\[([\s\S]*?)\]/);
              if (domainMatch) {
                try {
                  const domainSuggestions = JSON.parse('[' + domainMatch[1] + ']');
                  parsedData = {
                    domainSuggestions: domainSuggestions.map((d: any) => ({
                      domain: d.domain || '',
                      reasoning: d.reasoning || '',
                      tld: d.tld || 'com',
                    })),
                    businessRecommendations: [],
                    marketingTips: [],
                    nextSteps: [],
                  };
                } catch (extractError) {
                  console.error('Failed to extract partial data:', extractError);
                  return {
                    success: false,
                    message: 'Response was incomplete. Please try again with a shorter description.',
                  };
                }
              } else {
                console.error('Failed to parse or recover JSON:', recoveryError);
                return {
                  success: false,
                  message: 'Response was incomplete. Please try again with a shorter description.',
                };
              }
            }
          } else {
            return {
              success: false,
              message: 'No valid JSON found in response. Please try again.',
            };
          }
        }

        // Validate the structure
        if (!parsedData || !parsedData.domainSuggestions || !Array.isArray(parsedData.domainSuggestions)) {
          return {
            success: false,
            message: 'Invalid response format from Gemini API',
          };
        }

        // Ensure all required fields exist
        const validatedData: BusinessSuggestion = {
          domainSuggestions: parsedData.domainSuggestions || [],
          businessRecommendations: parsedData.businessRecommendations || [],
          marketingTips: parsedData.marketingTips || [],
          nextSteps: parsedData.nextSteps || [],
          relatedServices: parsedData.relatedServices || [],
        };

        return {
          success: true,
          data: validatedData,
        };
      } catch (fetchError) {
        // Network or other errors - try next model
        lastError = fetchError instanceof Error ? fetchError.message : 'Network error';
        continue;
      }
    }

    // If all models failed
    return {
      success: false,
      message: `All models failed. Last error: ${lastError}. Please check your API key and available models.`,
    };
  } catch (error) {
    console.error('Error calling Gemini API:', error);
    return {
      success: false,
      message:
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred while generating suggestions',
    };
  }
}
